import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { PalletToChangeTypes } from '../../modules/API/Pallets/models/palletToChange'
import { IPalletTypeItem } from '../../modules/API/Defaults/models/pallets'
import usePalletToChangeService from '../../../Services/Pallets/usePalletToChangeService'
import PalletToChangeItem from './Components/PalletToChangeItem'
import { FormShell, FormGrid, FormFooter } from '../ui/FormLayout'
import { AccentButton } from '../ui/Button'
import CustomBlockLoader from '../../main/second/mainbar/knisot/generic/CustomBlockLoader'
import PalletStackIcon from '../icons/IconLib/PalletStackIcon'

type Props = {
  id: number
  type: PalletToChangeTypes
  palletTypeList: IPalletTypeItem[]
  saveFunc?: () => void
  callback?: () => void
}

const PalletsToChange = ({
  id,
  type,
  palletTypeList,
  saveFunc,
  callback,
}: Props) => {
  const [t] = useTranslation('buttons')
  const {
    data,
    loading,
    onTypeChange,
    onQtyChange,
    onSave,
    clearData,
    checkForSave,
  } = usePalletToChangeService({ id, type })

  const handleAccept = async () => {
    await onSave(callback)
    saveFunc && saveFunc()
  }

  const palletQty = data?.reduce((acc, item) => acc + item.Quantity_Pallets, 0) ?? 0

  return (
    <FormShell>
      <SummaryBar>
        <SummaryIcon aria-hidden>
          <PalletStackIcon />
        </SummaryIcon>
        <SummaryLabel>סך הכל</SummaryLabel>
        <SummaryValue>{palletQty}</SummaryValue>
      </SummaryBar>

      {loading ? (
        <CustomBlockLoader />
      ) : (
        <FormGrid>
          {data?.map((item, index) => (
            <PalletToChangeItem
              key={item.RecID}
              index={index}
              palletTypeList={palletTypeList}
              onChangeQty={onQtyChange}
              onChangeType={onTypeChange}
              onClear={clearData}
              {...item}
            />
          ))}
        </FormGrid>
      )}

      {checkForSave && (
        <FormFooter>
          <AccentButton type='button' onClick={handleAccept} fullWidth={false}>
            {t('save', { ns: 'buttons' })}
          </AccentButton>
        </FormFooter>
      )}
    </FormShell>
  )
}

export default PalletsToChange

const SummaryBar = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.875rem;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.accentSoft};
  border: 1px solid ${({ theme }) => theme.colors.line};
  direction: rtl;
`

const SummaryIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.accentDeep};
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`

const SummaryLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink2};
`

const SummaryValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accentDeep};
  min-width: 1.5rem;
  text-align: center;
`

